import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, BookOpen, Code2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { v4 as uuidv4 } from "uuid";

/** Shared "W3Schools-style" manual notes editor — used by both Creator Studio
 * (course lecture notes) and Cloudivo Meet (recording notes). `endpoint` is the
 * full API path to PATCH, e.g. `/courses/{id}/notes` or `/meetings/recordings/{id}/notes`. */
export default function ManualNotesEditor({ endpoint, initialNotes, onSaved }) {
  const [notes, setNotes] = useState(initialNotes || []);
  const [saving, setSaving] = useState(false);

  const addNote = () => setNotes((prev) => [...prev, { id: uuidv4(), heading: "", content: "", code_example: "" }]);
  const updateNote = (id, field, value) => setNotes((prev) => prev.map((n) => (n.id === id ? { ...n, [field]: value } : n)));
  const removeNote = (id) => setNotes((prev) => prev.filter((n) => n.id !== id));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(endpoint, { manual_notes: notes });
      toast.success("Notes saved");
      onSaved?.(data.manual_notes);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass-panel rounded-2xl p-5" data-testid="manual-notes-editor">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-sm font-semibold text-white flex items-center gap-2"><BookOpen className="w-4 h-4 text-amber-400" /> Notes (W3Schools-style)</h3>
        <button onClick={addNote} data-testid="manual-notes-add-btn" className="flex items-center gap-1.5 text-xs font-semibold text-amber-300 hover:text-amber-200">
          <Plus className="w-3.5 h-3.5" /> Add Section
        </button>
      </div>
      <div className="space-y-4">
        {notes.map((n, i) => (
          <div key={n.id} className="relative pl-4 border-l-2 border-amber-400/40" data-testid={`manual-notes-item-${n.id}`}>
            <span className="absolute -left-[9px] top-0.5 w-4 h-4 rounded-full bg-amber-400 text-background text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
            <div className="border border-white/10 rounded-xl p-4 relative">
              <button onClick={() => removeNote(n.id)} data-testid={`manual-notes-remove-btn-${n.id}`} className="absolute top-3 right-3 text-slate-500 hover:text-rose-400">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <input value={n.heading} onChange={(e) => updateNote(n.id, "heading", e.target.value)} placeholder={`Section ${i + 1} heading`} data-testid={`manual-notes-heading-input-${n.id}`}
                className="w-full bg-transparent border-b border-white/10 pb-2 mb-3 text-sm font-semibold text-white outline-none focus:border-amber-400/50" />
              <textarea value={n.content} onChange={(e) => updateNote(n.id, "content", e.target.value)} placeholder="Explanation..." rows={3} data-testid={`manual-notes-content-input-${n.id}`}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50 resize-none mb-2" />
              <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1"><Code2 className="w-3 h-3" /> Example (optional)</label>
              <textarea value={n.code_example} onChange={(e) => updateNote(n.id, "code_example", e.target.value)} placeholder="Code / SOQL / config example" rows={2} data-testid={`manual-notes-code-input-${n.id}`}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-emerald-300 outline-none focus:border-amber-400/50 resize-none" />
            </div>
          </div>
        ))}
        {notes.length === 0 && <p className="text-xs text-slate-500">No sections yet — click "Add Section" to write your first note.</p>}
      </div>
      <button onClick={save} disabled={saving} data-testid="manual-notes-save-btn"
        className="mt-4 flex items-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-background font-bold text-sm px-4 py-2.5 rounded-lg transition-colors">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Notes
      </button>
    </div>
  );
}
